// =============================================================================
// io/sync/saveSessionContract.ts — the session contract write.
// -----------------------------------------------------------------------------
// THREE COLUMNS: the composite key and `session_contract`. Nothing else — same
// rule as the plan and recap writes, and the reason is sharper here.
//
// ── 🚨 WHAT THIS MUST NOT TOUCH ──
//
// The blob holds four timestamps written DURING the session by the P&L watcher:
// `dllHitAt`, `dllAcknowledgedAt`, `targetHitAt`, `targetAcknowledgedAt` — plus
// `manualPnl`. They are the record of whether the pledge held.
// `dllAcknowledgedAt` is string-compared against every later trade to flag it as
// an override of the trader's own daily stop, so overwriting one rewrites the
// history of a breach rather than losing a setting.
//
// `contractToBlob` merges over the row's own stored blob and writes only the five
// fields the form owns. `rawContract` on the read result is where `raw` comes
// from, and passing `{}` for a row that exists turns the merge back into a
// rebuild — which is exactly how those four timestamps would be nulled.
//
// ⚠ There is a test asserting the PAYLOAD KEY SET, not just its values (D62). A
// column added to this row later is a column this module silently starts
// overwriting, and the recap write shipped that bug once already.
// =============================================================================

import type { SupabaseClient } from '@supabase/supabase-js';
import { contractToBlob } from '../../domain/journal';
import type { SessionContract } from '../../domain/journal';
import { reportFailureAs } from '../diagnostics/errorLog';

/** Every column this module writes. Fixed, and asserted by a test. */
export const CONTRACT_COLUMNS = ['user_id', 'date', 'session_contract'] as const;

export interface SaveContractResult {
  updatedAt: string | null;
  error: string | null;
  heldStale: boolean;
}

export interface SaveContractArgs {
  sb: SupabaseClient;
  userId: string;
  date: string;
  contract: SessionContract;
  /** The row's stored blob. `{}` only for a date that has no row yet. */
  rawContract: Readonly<Record<string, unknown>>;
  baselineUpdatedAt: string | null;
}

export function contractRow(
  date: string,
  contract: SessionContract,
  rawContract: Readonly<Record<string, unknown>>,
  userId: string,
): Record<string, unknown> {
  return {
    user_id: userId,
    date,
    session_contract: contractToBlob(contract, rawContract),
  };
}

export async function saveSessionContract(
  args: SaveContractArgs,
): Promise<SaveContractResult> {
  const { sb, userId, date, contract, rawContract, baselineUpdatedAt } = args;
  const row = contractRow(date, contract, rawContract, userId);

  // ⚠ FAIL-OPEN stale check, identical to the plan and recap writes. A guard that
  // blocked saves it could not verify would lose real work on a transient blip.
  if (baselineUpdatedAt) {
    const { data, error } = await sb
      .from('journal_entries')
      .select('date, updated_at')
      .eq('user_id', userId)
      .eq('date', date);

    if (!error && Array.isArray(data) && data.length === 1) {
      const cloud = (data[0] as unknown as Record<string, unknown>)['updated_at'];
      if (typeof cloud === 'string'
        && new Date(cloud).getTime() > new Date(baselineUpdatedAt).getTime()) {
        return {
          updatedAt: null,
          heldStale: true,
          error:
            `This day's contract was changed somewhere else after you loaded it — ` +
            `probably the other client or another tab. Nothing was saved, so neither ` +
            `version is lost. Reload to see the newer copy, then re-apply your change.`,
        };
      }
    }
  }

  const { data, error } = await sb
    .from('journal_entries')
    .upsert(row, { onConflict: 'user_id,date' })
    .select('date, updated_at');

  if (error) {
    return {
      updatedAt: null,
      heldStale: false,
      error: reportFailureAs(
        'save session contract', error, `Save failed: ${error.message}`,
      ),
    };
  }

  if (!Array.isArray(data) || data.length !== 1) {
    return {
      updatedAt: null,
      heldStale: false,
      error:
        `The save returned ${Array.isArray(data) ? data.length : 'no'} rows instead of one, ` +
        `so it cannot be confirmed. Reload before editing again.`,
    };
  }

  const ts = (data[0] as unknown as Record<string, unknown>)['updated_at'];
  return { updatedAt: typeof ts === 'string' ? ts : null, heldStale: false, error: null };
}
